/**
 * Shared numeric / date sanity ranges + inline-error helpers for profile forms.
 *
 * BUG_LIST #189 / #190: number inputs across the profile edit modals accepted
 * garbage (SAT 9999, GPA 4.8 on a 4.0 scale, negative sprint times, 999 mph,
 * 1800s birthdates) because `maxLength` is a no-op on <input type="number">
 * and nothing validated the value. These helpers return a human-readable
 * error string (or null when the value is acceptable / empty) so each modal
 * can render an inline error and block Save.
 *
 * An EMPTY string is always valid here — empty means "cleared / not provided",
 * which the backend treats as a delete. Only a present-but-out-of-range value
 * is an error.
 */

export interface NumericRange {
  min: number;
  max: number;
}

// Test-score ranges (US college recruiting).
export const RANGE_SAT_SECTION: NumericRange = { min: 200, max: 800 };
export const RANGE_SAT_TOTAL: NumericRange = { min: 400, max: 1600 };
export const RANGE_PSAT_SECTION: NumericRange = { min: 160, max: 760 };
export const RANGE_PSAT_TOTAL: NumericRange = { min: 320, max: 1520 };
export const RANGE_ACT: NumericRange = { min: 1, max: 36 };

// Physical ranges.
export const RANGE_HEIGHT_FEET: NumericRange = { min: 3, max: 8 };
export const RANGE_HEIGHT_INCHES: NumericRange = { min: 0, max: 11 };
export const RANGE_WEIGHT_LBS: NumericRange = { min: 40, max: 500 };

// Performance-metric ranges.
export const RANGE_SPRINT_TIME_SEC: NumericRange = { min: 0.5, max: 60 };
export const RANGE_POP_TIME_SEC: NumericRange = { min: 0.5, max: 6 };
export const RANGE_VELOCITY_MPH: NumericRange = { min: 10, max: 120 };
export const RANGE_SPIN_RATE_RPM: NumericRange = { min: 100, max: 4000 };
export const RANGE_GRIP_STRENGTH_LBS: NumericRange = { min: 0, max: 250 };

/**
 * Returns an error string if `raw` is present and out of range, otherwise null.
 * Empty / whitespace is treated as "cleared" and returns null.
 */
export function rangeError(
  raw: string | number | null | undefined,
  range: NumericRange,
  opts?: { integer?: boolean; unit?: string },
): string | null {
  if (raw === null || raw === undefined) return null;
  const str = String(raw).trim();
  if (str === '') return null;

  const num = Number(str);
  if (!Number.isFinite(num)) return 'Enter a valid number';
  if (opts?.integer && !Number.isInteger(num)) return 'Enter a whole number';

  const unit = opts?.unit ? ` ${opts.unit}` : '';
  if (num < range.min || num > range.max) {
    return `Enter a value between ${range.min} and ${range.max}${unit}`;
  }
  return null;
}

/**
 * GPA validated against its own scale (default 4.0). Rejects e.g. 4.8 on a 4.0
 * scale. Empty is valid (cleared).
 */
export function gpaError(raw: string | number | null | undefined, scale: number | string = 4.0): string | null {
  if (raw === null || raw === undefined) return null;
  const str = String(raw).trim();
  if (str === '') return null;
  const num = Number(str);
  if (!Number.isFinite(num)) return 'Enter a valid GPA';
  const scaleNum = Number(scale) || 4.0;
  if (num < 0) return 'GPA cannot be negative';
  if (num > scaleNum) return `GPA cannot exceed the ${scaleNum.toFixed(1)} scale`;
  return null;
}

/**
 * Birthdate (YYYY-MM-DD or ISO). Rejects future dates and implausible ages.
 * Empty is valid (cleared).
 */
export function birthdateError(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const str = String(raw).trim();
  if (str === '') return null;
  // Parse date-only without timezone shift.
  const datePart = str.split('T')[0];
  const parsed = new Date(`${datePart}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return 'Enter a valid date';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (parsed.getTime() > today.getTime()) return 'Birthdate cannot be in the future';

  const ageMs = today.getTime() - parsed.getTime();
  const ageYears = ageMs / (365.25 * 24 * 60 * 60 * 1000);
  if (ageYears > 100) return 'Enter a valid birthdate';
  if (ageYears < 5) return 'Enter a valid birthdate';
  return null;
}

/** True when every value in the map is null (no errors). */
export function hasNoErrors(errors: Record<string, string | null | undefined>): boolean {
  return Object.values(errors).every((e) => !e);
}
